import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { history, Link } from '@umijs/max';

import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { fetchCurrentUserSafe } from '@/services/user';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { SwitchTheme } from './components/RightContent';
import { ChatSystem } from './components/ChatSystem';

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const user = await fetchCurrentUserSafe({ skipErrorHandler: true });
      return user as API.CurrentUser;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  // 如果不是登录页面，执行
  const { location } = history;
  const currentUser = await fetchUserInfo();
  if (location.pathname !== loginPath) {
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    onMenuHeaderClick: () => {},
    actionsRender: () => [<SwitchTheme />],//() => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: (initialState?.currentUser as API.ExtendedUser)?.avatar || undefined, // Use ExtendedUser type for avatar
      title: initialState?.currentUser?.name || 'User',
      render: (_, avatarChildren) => {
        return (
          <AvatarDropdown>{avatarChildren}</AvatarDropdown>
        );
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.email,
      markStyle: {
        opacity: 0.3
      }
    },
    // menuItemRender: (menuItemProps, defaultDom) => {
    //   if (
    //     menuItemProps.isUrl ||
    //     !menuItemProps.path ||
    //     location.pathname === menuItemProps.path
    //   ) {
    //     return defaultDom;
    //   }
    //   return <Link to={menuItemProps.path.replace(':appSlug', (initialState?.currentApp?.slug || ''))}>{defaultDom}</Link>;
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDevOrTest
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <QueryClientProvider client={queryClient}>
            {children}
            {/* AI Chat System - only show for authenticated users */}
            {initialState?.currentUser && (
              <ChatSystem
                buttonTooltip="Chat with AI Assistant"
                maxConversations={5}
                buttonPosition={{ bottom: 24, right: 24 }}
                buttonSize="large"
                onError={(error) => {
                  console.error('Chat system error:', error);
                }}
              />
            )}
          </QueryClientProvider>
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: isDev ? '' : 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
